import { useEffect, useMemo, useRef, useState } from 'react'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

// `title` earns its place: a card's chip shows the most urgent state, and its
// tooltip lists every open task by name so nothing is hidden behind a count.
const TASK_FIELDS = `id status dueDate contactId title`

/** What a card says about the work already promised against a person. */
export type BoardTask = { id: string; status?: string; dueDate?: string; contactId?: string; title?: string }

/**
 * Open tasks for everyone on the board, in one request.
 *
 * A task assigned to somebody is the strongest reason not to ring them, or
 * the strongest reason to: either a colleague has already committed to this
 * person, or a commitment has been missed. Either way it belongs on the card,
 * and the focus card's own `useContactTasks` cannot serve it here, because a
 * request per card is a hundred requests for one board.
 *
 * One query for every id on screen, re-run when the set of ids changes.
 */
/**
 * The open tasks, per contact.
 *
 * The whole task rather than a count: a card's chip says when the work is due
 * ("Overdue · Aug 12", "Due Sep 2"), and a count cannot answer that. Nothing
 * here decides how late anything is; `app/lib/taskBadges` owns that, and owns
 * it in whole calendar days, which is the only correct way to read a due date.
 */
export function groupOpenTasks(tasks: BoardTask[]): Map<string, BoardTask[]> {
  const byContact = new Map<string, BoardTask[]>()
  for (const task of tasks) {
    if (!task.contactId || task.status !== `open`) continue
    const current = byContact.get(task.contactId)
    if (current) current.push(task)
    else byContact.set(task.contactId, [task])
  }
  return byContact
}

const EMPTY: ReadonlyMap<string, BoardTask[]> = new Map()

/**
 * @param reloadKey Change it to re-read. The set of contacts on a board does
 *   not change when somebody adds a task to one of them, so without a signal
 *   of its own this would sit on the answer it got the first time and a task
 *   created in the drawer would not reach the card behind it.
 */
export function useBoardTasks(contactIds: string[], reloadKey = 0): ReadonlyMap<string, BoardTask[]> {
  const [summary, setSummary] = useState<ReadonlyMap<string, BoardTask[]>>(EMPTY)
  const generation = useRef(0)
  // A primitive the effect can depend on: the array is rebuilt on every
  // render of the board, and depending on it directly would refetch forever.
  const key = useMemo(() => [...contactIds].sort().join(`,`), [contactIds])

  useEffect(() => {
    generation.current += 1
    const token = generation.current
    const ids = key ? key.split(`,`) : []
    if (!ids.length) {
      setSummary(EMPTY)
      return
    }
    bg(
      gqlFetchList<BoardTask>(
        `tasks`,
        {
          // `status:open` is verified against the live index rather than
          // assumed: 212 of one workspace's 9,357 tasks come back for it. It
          // matters at that size, because fetching every task a contact ever
          // had and filtering here would spend the page limit on finished
          // work and quietly lose the open task at the end of it.
          //
          // The ids go in one OR group, the same shape the board already
          // uses for a location drill-down.
          search: `status:open contactId:(${ids.join(` OR `)})`,
          pagination: { limit: 500 },
        },
        TASK_FIELDS,
      )
        .then(({ hits }) => {
          if (token !== generation.current) return
          setSummary(groupOpenTasks(hits ?? []))
        })
        .catch((err) => {
          if (token !== generation.current) return
          // Quiet: a card without its task chip is still a usable card, and a
          // toast about a decoration nobody asked for is worse than the gap.
          logError(`Action Center could not load tasks for the board`, err)
        }),
    )
  }, [key, reloadKey])

  return summary
}
